import { styled } from '@chronosphereio/chrono-ui';

export interface CrosshairLineProps {
  pixelX: number | undefined;
  height: number;
  top?: number;
  /** Horizontal offset added to `pixelX` (e.g. gridRect.x for chart plot areas). */
  left?: number;
}

const Line = styled('div')(({ theme }) => ({
  position: 'absolute',
  top: 0,
  left: 0,
  width: '1px',
  backgroundColor: theme.palette.border.bold,
  pointerEvents: 'none',
  zIndex: 1,
}));

/**
 * Thin vertical line rendered as a DOM overlay to indicate the shared crosshair position.
 * Positioned absolutely within a `position: relative` container that represents the chart's
 * plot area. Hidden when `pixelX` is undefined or out of the [0, container-width] range.
 *
 * `pixelX` is always the coordinate *within the plot area*. Use the `left` and `top` props
 * when the plot area is offset inside the container (e.g. HeatMap passes `gridRect.x/y`).
 * If the coordinate system already accounts for the container origin (e.g. ECharts'
 * `convertToPixel`), omit `left`/`top` and pass `pixelX` directly.
 */
export function CrosshairLine({ pixelX, height, top = 0, left = 0 }: CrosshairLineProps) {
  if (pixelX === undefined || pixelX < 0) return null;

  return <Line data-testid="crosshair-line" style={{ transform: `translate(${pixelX + left}px, ${top}px)`, height }} />;
}
